// ===== Services =====

import hasSlateContent from './hasSlateContent';

// ===== Interfaces =====

import {
    IReadPostItem,
    IReadPostChapterItem,
} from '../interfaces';

// ===== Enums =====

import {
    POST_CONTENT_SCOPE,
} from '../enums';

export interface IPostVersionHistoryScopeSelection {
    scope: POST_CONTENT_SCOPE,
    chapterId: string | null,
    sectionId: string | null,
}

export interface IResolvedPostVersionHistoryScope {
    rootAvailable: boolean,
    chapterOptions: IReadPostChapterItem[],
    sectionChapterOptions: IReadPostChapterItem[],
    selection: IPostVersionHistoryScopeSelection,
}

const chapterHasStandaloneHistory = (chapter: IReadPostChapterItem): boolean => {
    const hasSections = (chapter.sections || []).length > 0;
    return !hasSections || hasSlateContent(chapter.value || '');
};

const resolvePostVersionHistoryScope = (
    post: IReadPostItem | null | undefined,
    preferredSelection: IPostVersionHistoryScopeSelection,
): IResolvedPostVersionHistoryScope => {
    const postChapters = post?.chapters || [];
    const rootAvailable = postChapters.length === 0 || hasSlateContent(post?.value || '');
    const chapterOptions = postChapters.filter(chapterHasStandaloneHistory);
    const sectionChapterOptions = postChapters.filter((chapter) => (chapter.sections || []).length > 0);

    const preferredChapter = postChapters.find((chapter) => chapter.id === preferredSelection.chapterId)
        || sectionChapterOptions[0]
        || chapterOptions[0]
        || postChapters[0]
        || null;
    const preferredSection = preferredChapter?.sections?.find((section) => section.id === preferredSelection.sectionId)
        || preferredChapter?.sections?.[0]
        || null;

    if (
        preferredSelection.scope === POST_CONTENT_SCOPE.section
        && preferredChapter
        && sectionChapterOptions.some((chapter) => chapter.id === preferredChapter.id)
        && preferredSection
    ) {
        return {
            rootAvailable,
            chapterOptions,
            sectionChapterOptions,
            selection: {
                scope: POST_CONTENT_SCOPE.section,
                chapterId: preferredChapter.id,
                sectionId: preferredSection.id,
            },
        };
    }

    if (
        preferredSelection.scope === POST_CONTENT_SCOPE.chapter
        && preferredChapter
        && chapterOptions.some((chapter) => chapter.id === preferredChapter.id)
    ) {
        return {
            rootAvailable,
            chapterOptions,
            sectionChapterOptions,
            selection: {
                scope: POST_CONTENT_SCOPE.chapter,
                chapterId: preferredChapter.id,
                sectionId: preferredSection?.id || null,
            },
        };
    }

    if (
        preferredSelection.scope === POST_CONTENT_SCOPE.root
        && rootAvailable
    ) {
        return {
            rootAvailable,
            chapterOptions,
            sectionChapterOptions,
            selection: {
                scope: POST_CONTENT_SCOPE.root,
                chapterId: preferredChapter?.id || null,
                sectionId: preferredSection?.id || null,
            },
        };
    }

    if (chapterOptions.length > 0) {
        const nextChapter = chapterOptions.find((chapter) => chapter.id === preferredChapter?.id)
            || chapterOptions[0];

        return {
            rootAvailable,
            chapterOptions,
            sectionChapterOptions,
            selection: {
                scope: POST_CONTENT_SCOPE.chapter,
                chapterId: nextChapter.id,
                sectionId: nextChapter.sections?.[0]?.id || null,
            },
        };
    }

    if (sectionChapterOptions.length > 0) {
        const nextChapter = sectionChapterOptions.find((chapter) => chapter.id === preferredChapter?.id)
            || sectionChapterOptions[0];

        return {
            rootAvailable,
            chapterOptions,
            sectionChapterOptions,
            selection: {
                scope: POST_CONTENT_SCOPE.section,
                chapterId: nextChapter.id,
                sectionId: nextChapter.sections?.[0]?.id || null,
            },
        };
    }

    return {
        rootAvailable,
        chapterOptions,
        sectionChapterOptions,
        selection: {
            scope: POST_CONTENT_SCOPE.root,
            chapterId: preferredChapter?.id || null,
            sectionId: preferredSection?.id || null,
        },
    };
};

export default resolvePostVersionHistoryScope;
